import { FormHelperText, Grid } from "@material-ui/core";
import { produce } from "immer";
import { isNil } from "lodash";
import React, { useEffect, useState } from "react";
import { isEmpty } from "underscore";
import { selectEnableFleetVehicleScheduling, selectUseBulkVehicleAssignments, useGetOccupiedParkingRightsCount } from "../../../containers/FleetManager/MyProducts/ParkingRightsDataTable/common";
import { IRightSelection } from "../../../hooks/useValidationSchema";
import { ParkingProductModel, ParkingProductType } from "../../../models/ParkingProductModel";
import { useAppSelector } from "../../../store";
import { variables } from "../../../theme/variables";
import Heading from "../../../ui/Heading/Heading";
import ToggleSelectAll from "../ToggleSelectAll/ToggleSelectAll";
import ResultsGrid from "./ResultsGrid/ResultsGrid";
import "./_parkingRights.scss";

const { typography } = variables;

type ErrorType = {
  hasError: boolean;
  message: string | string[] | undefined;
};

const initialState: ICheckParkingRight[] = [];

export interface IParkingRightsGridProps {
  data: ParkingProductModel[] | null;
  initiallySelectedRights?: IRightSelection[];
  disableProductsWithoutFirstAvailableContractStartDate?: boolean;
  loading?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onStateChange?: (state: typeof initialState) => void;
  onSelectAll: (rights: IRightSelection[]) => void;
  onDeselectAll: () => void;
  error?: ErrorType;
  title: string;
  required: boolean;
  selectAllTooltipText: string;
  deselectAllTooltipText: string;
  selectAllText: string;
  deselectAllText: string;
  renderRowExpansion?: (pr: ICheckParkingRight) => React.ReactNode;
}

export type ICheckParkingRight = {
  id: number;
  right?: ParkingProductModel;
  checked: boolean;
  disabled: boolean;
  activationDate?: Date | null;
  expirationDate?: Date | null;
  hasFutureCapacities?: boolean;
};

function isChecked(right: ParkingProductModel, selectedRights?: IRightSelection[]): boolean {
  return selectedRights?.some(r => r.pmcId === right.pmcId && r.contractType === right.contractType) ?? false;
}

const ParkingRightsGrid = (props: IParkingRightsGridProps) => {
  const [state, setState] = useState(initialState);

  const getTotalOccupiedParkingRights = useGetOccupiedParkingRightsCount();
  const useBulkVehicleAssignments = useAppSelector(selectUseBulkVehicleAssignments);  
  const enableFleetVehicleScheduling = useAppSelector(selectEnableFleetVehicleScheduling);
  

  const onChecked = (event: React.ChangeEvent<HTMLInputElement>, pr: ICheckParkingRight) => {
    setState(produce(draft => {
        const right = draft.find((x) => x.id === pr.id && x.right?.contractType === pr.right?.contractType);
        const { checked } = event.currentTarget;
        if (right) {
          right.checked = checked;
        }
    }));
    props.onChange(event);
  };

  const onSelectAll = () => {
    const newState = state.map((pr) => {
      if (pr?.checked) {
        return pr;
      }

      if (!pr?.disabled) {
        return {
          ...pr,
          checked: true,
        };
      }

      return pr;
    });

    props.onSelectAll(newState.filter((pr) => pr.checked).map((pr) => ({ pmcId: pr.right?.pmcId!, contractType: pr.right?.contractType! })));

    setState(newState);
  };

  const onDeselectAll = () => {
    setState((state) =>
      state.map((pr) => {
        if (!pr.checked) {
          return pr;
        }

        return { ...pr, checked: false };
      })
    );
    props.onDeselectAll();
  };

  const isParkingRightDisabled = (
    parkingProductModel: ParkingProductModel,
    isAssignedOrDelegated: boolean
  ): boolean => {
    if (props.disableProductsWithoutFirstAvailableContractStartDate && !isAssignedOrDelegated && !parkingProductModel.firstAvailableContractStartDate) {
      return true;
    }

    if(parkingProductModel.productType == ParkingProductType.PedestrianAccess){
      return parkingProductModel.totalDelegatedPedestrianRights >= parkingProductModel.totalPedestrianRights && !isAssignedOrDelegated;
    }

    if (useBulkVehicleAssignments && parkingProductModel?.hasUnlimitedEntryRights) {
      return false;
    }
    if (
      parkingProductModel?.totalDelegatedParkingRights !== undefined &&
      parkingProductModel?.totalParkingRights !== undefined
    ) {
      return (
        enableFleetVehicleScheduling ? false :
          getTotalOccupiedParkingRights(parkingProductModel) >=
          parkingProductModel.totalParkingRights && !isAssignedOrDelegated
      );
    }

    return true;
  };

  useEffect(() => {
    if (!isNil(props.data) && !isEmpty(props.data)) {
      setState(
        props.data.map((right) => {
          const checked = isChecked(right, props.initiallySelectedRights);
          const disabled = isParkingRightDisabled(right, checked);
          return {
            id: right.pmcId ?? 0,
            right,
            checked,
            disabled,
            hasFutureCapacities: right.hasFutureCapacities,
          };
        })
      );
    }
    return () => setState(initialState);
    // eslint-disable-next-line
  }, [props.data, props.initiallySelectedRights]);

  useEffect(() => {
    if (props.onStateChange) {
      props.onStateChange(state);
    }
  }, [state]);

  const showSelectAll = () =>
    state.every((parkingRight) => parkingRight.checked || parkingRight.disabled);

  return (
    <Grid container direction="row" classes={{ root: "parking-rights-wrapper" }}>
      <Grid container item direction="column" xs={6}>
        <Heading required={props.required} fontSize={typography.fontSizeBase}>
          {props.title}
        </Heading>
      </Grid>
      <Grid container item direction="column" xs={6} alignItems="flex-end">
        <ToggleSelectAll
          showSelectAll={showSelectAll()}
          selectAllTooltipText={props.selectAllTooltipText}
          deselectAllTooltipText={props.deselectAllTooltipText}
          selectAllText={props.selectAllText}
          deselectAllText={props.deselectAllText}
          onSelectAll={onSelectAll}
          onDeselectAll={onDeselectAll}
        />
      </Grid>
      <Grid container item direction="column" xs={12}>
        <FormHelperText error={props.error?.hasError}>{props.error?.message}</FormHelperText>
      </Grid>

      <ResultsGrid loading={props.loading} state={state} onChecked={onChecked} renderRowExpansion={props.renderRowExpansion} />
    </Grid>
  );
};

export default ParkingRightsGrid;
